import {
  Box,
  DragDrop,
  FormControl,
  FormControlLabel,
  FormLabel,
  GKButton,
  GKDatePicker,
  GKEditor,
  GKRadio,
  GKSearchSelect,
  GKTextField,
  GKTypography,
  Grid2,
  RadioGroup,
  Show,
  Typography,
} from "components";
import dayjs from "dayjs";
import { useFetchQuery, useMutation } from "hooks/useQueries";
import {
  EquityBasketListInterface,
  SingleStockItemInterface,
} from "interfaces/EquityBasket";
import DashboardLayout from "layouts/DashboardLayout";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import axiosInstance from "utils/axiosInstance";
import { handleCreateFormData } from "utils/formHelpers";
import { refetchQuery } from "utils/helpers";
import {
  riskDropdown,
  timePerdioDropdown,
  volatilityDropdown,
} from "../equityBasketHelper";

const AssetEditModal = () => {
  const [uploadError, setUploadError] = useState(null);

  const { id, assetId } = useParams();
  /** ========================================================================================================= */
  const { data } = useFetchQuery({
    key: ["GET_EQUITY_BASKET_DETAILS_DATA"],
    route: `/model_portfolio/${id}/details/`,
    enabled: Boolean(id),
  });

  const basketDetails: EquityBasketListInterface = data?.data;

  const form = useForm({
    defaultValues: {
      stockName: "",
      date: new Date(),
      recommendedSellPrice: "",
      recommendedBuyPrice: "",
      recommendationType: "",
      maxInvestmentAmount: "",
      rationale: "",
      status: "",
      riskProfile: "",
      published: false,
      timePeriod: "",
      volatility: "",
      targetPrice: "",
      limitPrice: "",
      deviation: "",
      stopLoss: "",
      research_files: [],
    },
  });

  /** ========================================================================================================= */
  const { data: recommendationDetails } = useFetchQuery<{
    data: SingleStockItemInterface;
  }>({
    key: ["GET_MODEL_PORTFOLIO_DETAILS_DATA", assetId],
    route: `/model_portfolio/${id}/stock_item/${assetId}/`,
    enabled: Boolean(assetId),
    success: (response: any) => {
      form.reset({ ...response.data });
    },
  });

  const { mutate: handleSubmit, isLoading: submitLoading } = useMutation({
    mutationKey: ["UPDATE_PORTFOLIO_DETAILS"],
    mutationFn: (data: any) =>
      axiosInstance.patch(
        `/model_portfolio/${id}/stock_item/${assetId}/`,
        data,
      ),
    onSuccess: () => {
      refetchQuery("GET_STOCK_DETAILS");
    },
  });

  const onSubmit = (values: any) => {
    handleSubmit(
      handleCreateFormData({
        dirtyFields: form.formState.dirtyFields,
        values: values,
      }),
    );
  };

  form.watch();

  return (
    <DashboardLayout title="Add Recommendation">
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Grid2 container spacing={2}>
          <Box maxHeight={`calc(100vh - 170px)`} overflow={"auto"}>
            <Grid2 container size={6} spacing={2}>
              <Grid2 size={12}>
                <Typography fontSize={18} fontWeight={400}>
                  {recommendationDetails?.data?.name}
                </Typography>
              </Grid2>
              <Show.When isTrue={basketDetails?.basketType !== "Asset"}>
                <Grid2 size={12}>
                  <Controller
                    name="recommendationType"
                    control={form.control}
                    render={({ field }) => (
                      <FormControl>
                        <FormLabel>Recommendation Type:</FormLabel>
                        <RadioGroup row {...field}>
                          <FormControlLabel
                            value="BUY"
                            control={<GKRadio />}
                            label="Buy"
                          />
                          <FormControlLabel
                            value="HOLD"
                            control={<GKRadio />}
                            label="Hold"
                          />
                          <FormControlLabel
                            value="SELL"
                            control={<GKRadio />}
                            label="Sell"
                          />
                        </RadioGroup>
                      </FormControl>
                    )}
                  />
                </Grid2>
              </Show.When>
              <Show.When isTrue={basketDetails?.basketType === "Model"}>
                <Grid2 size={12}>
                  <GKTypography variant="primaryDark">
                    Price & Market Inputs
                  </GKTypography>
                </Grid2>
                <Grid2 size={12}>
                  <Controller
                    name="date"
                    control={form.control}
                    render={({ field, fieldState: { error } }) => (
                      <GKDatePicker
                        {...field}
                        inputLabel="Updation Date"
                        value={dayjs(field.value)}
                        onChange={(value) => {
                          field.onChange(dayjs(value).format("YYYY-MM-DD"));
                        }}
                        slotProps={{
                          textField: {
                            error: !!error,
                            helperText: !!error && error?.message,
                          },
                        }}
                      />
                    )}
                  />
                </Grid2>
                <Grid2 size={6}>
                  <Controller
                    name="targetPrice"
                    control={form.control}
                    render={({ field, fieldState: { error } }) => (
                      <GKTextField
                        {...field}
                        inputLabel="Target Price"
                        slotProps={{ input: { endAdornment: "₹" } }}
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                </Grid2>
                <Grid2 size={6}>
                  <Controller
                    name="stopLoss"
                    control={form.control}
                    render={({ field, fieldState: { error } }) => (
                      <GKTextField
                        {...field}
                        inputLabel="Stop Loss"
                        slotProps={{ input: { endAdornment: "₹" } }}
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                </Grid2>
                <Grid2 size={6}>
                  <Controller
                    name="limitPrice"
                    control={form.control}
                    render={({ field, fieldState: { error } }) => (
                      <GKTextField
                        {...field}
                        inputLabel="Limit Price"
                        slotProps={{ input: { endAdornment: "₹" } }}
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                </Grid2>
                <Grid2 size={6}>
                  <Controller
                    name="maxInvestmentAmount"
                    control={form.control}
                    render={({ field, fieldState: { error } }) => (
                      <GKTextField
                        {...field}
                        inputLabel="Max. Investment Amount"
                        slotProps={{ input: { endAdornment: "₹" } }}
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                </Grid2>
                <Grid2 size={6}>
                  <Controller
                    name="recommendedBuyPrice"
                    control={form.control}
                    render={({ field, fieldState: { error } }) => (
                      <GKTextField
                        {...field}
                        inputLabel="Rec. Buy Price"
                        slotProps={{ input: { endAdornment: "₹" } }}
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                </Grid2>
                <Grid2 size={6}>
                  <Controller
                    name="recommendedSellPrice"
                    control={form.control}
                    render={({ field, fieldState: { error } }) => (
                      <GKTextField
                        {...field}
                        inputLabel="Rec. Sell Price"
                        slotProps={{ input: { endAdornment: "₹" } }}
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                </Grid2>

                <Grid2 size={6} display={"flex"} alignItems={"center"}>
                  <Typography color="var(neutral-900)">
                    Deviation:{" "}
                    <Typography component={"span"} fontWeight={600}>
                      {form.watch("deviation") || "-"}
                    </Typography>
                  </Typography>
                </Grid2>
              </Show.When>
              <Grid2 size={12}>
                <GKTypography variant="primaryDark">
                  Investment Parameters
                </GKTypography>
              </Grid2>
              <Grid2 size={12}>
                <Controller
                  name="timePeriod"
                  control={form.control}
                  render={({ field }) => (
                    <FormControl>
                      <FormLabel>Time Horizon:</FormLabel>
                      <RadioGroup row {...field}>
                        {timePerdioDropdown?.map((dataItem) => (
                          <FormControlLabel
                            value={dataItem.value}
                            control={<GKRadio />}
                            label={dataItem?.name}
                            key={dataItem?.name}
                          />
                        ))}
                      </RadioGroup>
                    </FormControl>
                  )}
                />
              </Grid2>
              <Grid2 size={12}>
                <Controller
                  name="volatility"
                  control={form.control}
                  render={({ field }) => (
                    <FormControl>
                      <FormLabel>Volatility:</FormLabel>
                      <RadioGroup row {...field}>
                        {volatilityDropdown?.map((dataItem) => (
                          <FormControlLabel
                            value={dataItem.value}
                            control={<GKRadio />}
                            label={dataItem?.name}
                            key={dataItem?.name}
                          />
                        ))}
                      </RadioGroup>
                    </FormControl>
                  )}
                />
              </Grid2>
              <Grid2 size={12}>
                <Controller
                  name="riskProfile"
                  control={form.control}
                  render={({ field }) => (
                    <GKSearchSelect
                      multiple
                      {...field}
                      disableClearable={false}
                      onChange={(e, val) => {
                        field.onChange(val);
                      }}
                      value={field.value}
                      options={riskDropdown}
                      inputLabel="Risk Profile"
                    />
                  )}
                />
              </Grid2>
              <Grid2 size={12}>
                <GKTypography variant="primaryDark">
                  Rationale & Research
                </GKTypography>
              </Grid2>
              <Grid2 size={12}>
                <Controller
                  name="rationale"
                  control={form.control}
                  render={({ field, fieldState: { error } }) => (
                    <GKEditor
                      {...field}
                      inputLabel="Rationale"
                      placeholder="Specify key screening factors like market cap, growth metrics, and sector focus, based on research-driven criteria."
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid2>
              <Grid2 size={12}>
                <GKTypography variant="primaryLight">
                  Research Files
                </GKTypography>
              </Grid2>
              <Grid2 size={12}>
                <Controller
                  name={`research_files`}
                  control={form.control}
                  render={({ field, fieldState: { error } }) => (
                    <DragDrop
                      {...field}
                      uploadError={uploadError}
                      setUploadError={setUploadError}
                      value={field.value}
                      onChange={(e) => {
                        field.onChange(e);
                      }}
                      supportName=".png, .jpg, .pdf"
                      acceptType={".pdf,.png,.jpg"}
                      error={!!error}
                      multiple
                    />
                  )}
                />
              </Grid2>
            </Grid2>
          </Box>
          <Grid2
            size={12}
            textAlign={"right"}
            borderTop={`solid 1px var(--neutral-100)`}
            p={1}
            pb={0}
          >
            <GKButton
              variant="contained"
              type="submit"
              loading={submitLoading}
              disabled={!form.formState.isDirty}
            >
              Save
            </GKButton>
          </Grid2>
        </Grid2>
      </form>
    </DashboardLayout>
  );
};

export default AssetEditModal;
